/**
 * Curated team picker for `linked`-mode demo orgs (e.g. Monaco).
 *
 * Backend's `selectableTeams` query returns the curated `OrganizationTeam`
 * rows where `selectableAsActiveTeam = true` in this mode (the `query` arg
 * is ignored). Typically 2–5 entries — render as a vertical list, no search.
 * Uses `cache-and-network` so admin-side curation changes propagate quickly.
 * @module features/team-selection/components/TeamSelectorLinked
 */
import React, { useCallback, useMemo } from "react";

import TeamSelectorLinkedView, {
  TeamSelectorLinkedRow,
} from "./TeamSelectorLinkedView";

import { Box } from "@/components/atoms/Box";
import { Spinner } from "@/components/atoms/Spinner";
import { Text } from "@/components/atoms/Text";
import {
  useTeamSelectionSelectableTeamsQuery,
  type TeamSelectionTeamFragment,
} from "@/generated/graphql";
import { formatCountrySegment } from "@/features/team-selection/utils/countryFlag";
import { useTeamSelection } from "@/providers/TeamSelectionProvider";

/**
 * Optional props for the linked-mode picker container.
 */
interface Props {
  /** Optional callback fired after a successful pick (e.g. close modal). */
  readonly onSelected?: () => void;
}

/**
 * Builds the secondary "<League> · <flag> <Country>" line shown beneath the
 * team name. Returns null when neither league name nor country is known so
 * the row collapses gracefully back to a single line.
 * @param league - The team's league fragment (may be null)
 * @returns Joined league + country string, or null when nothing to show
 */
const buildLeagueSubtitle = (
  league: TeamSelectionTeamFragment["league"]
): string | null => {
  if (!league) return null;
  const segments = [league.name, formatCountrySegment(league.country)].filter(
    (segment): segment is string => Boolean(segment)
  );
  return segments.length > 0 ? segments.join(" · ") : null;
};

/**
 * Container for the linked-mode picker. Wires the `selectableTeams` query
 * to the View, branches on loading / error / empty states, and routes
 * selection through `switchTeam`.
 * @param root0 - Component props
 * @param root0.onSelected - Optional callback fired after a successful pick
 * @returns The rendered linked-mode picker (or an inline state placeholder)
 */
const TeamSelectorLinkedContainer = ({ onSelected }: Props) => {
  const { activeTeamId, switchTeam } = useTeamSelection();
  const { data, loading, error } = useTeamSelectionSelectableTeamsQuery({
    variables: { first: 50 },
    fetchPolicy: "cache-and-network",
  });

  const handleSelect = useCallback(
    async (teamId: string, teamName: string) => {
      // No-op when the user re-selects the active team — switchTeam
      // would otherwise reset the Apollo store and fire a duplicate
      // `team_switched` event for a non-switch.
      if (teamId === activeTeamId) {
        onSelected?.();
        return;
      }
      await switchTeam(teamId, teamName);
      onSelected?.();
    },
    [activeTeamId, switchTeam, onSelected]
  );

  const rows = useMemo<readonly TeamSelectorLinkedRow[]>(() => {
    const edges = data?.selectableTeams.edges ?? [];
    return edges.map(edge => ({
      id: edge.node.id,
      name: edge.node.name,
      isActive: edge.node.id === activeTeamId,
      subtitle: buildLeagueSubtitle(edge.node.league),
      onPress: () => handleSelect(edge.node.id, edge.node.name),
    }));
  }, [data, activeTeamId, handleSelect]);

  if (loading && rows.length === 0) {
    return (
      <Box className="items-center py-6">
        <Spinner />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="py-6">
        <Text variant="unstyled" className="text-status-error">
          Couldn't load selectable teams.
        </Text>
      </Box>
    );
  }

  if (rows.length === 0) {
    return (
      <Box className="py-6">
        <Text variant="unstyled" className="text-content-muted">
          No selectable teams configured for this organization.
        </Text>
      </Box>
    );
  }

  return <TeamSelectorLinkedView rows={rows} />;
};

export default TeamSelectorLinkedContainer;
